import { truncateForJsonByteBudget as truncatePendoString } from './payload-size'

export { MAX_SEARCH_EVENT_JSON_BYTES } from './payload-size'
export {
  buildSearchFailedPayload,
  buildSearchSessionEndedPayload,
} from './search-diagnostics-outcomes'

export const SEARCH_DIAGNOSTICS_EVENTS = {
  resultsRendered: 'SearchResultsRendered',
  noResults: 'SearchNoResults',
  resultClicked: 'SearchResultClicked',
  reformulated: 'SearchReformulated',
  sessionEnded: 'SearchSessionEnded',
  failed: 'SearchFailed',
  didYouMeanShown: 'SearchDidYouMeanShown',
  didYouMeanSelected: 'SearchDidYouMeanSelected',
} as const

const MAX_RENDERED_QUERY_LENGTH = 50
const MAX_RETRIED_RENDERED_QUERY_LENGTH = 25
const MAX_RENDERED_TITLE_LENGTH = 40
const MAX_CLICK_QUERY_LENGTH = 50
const MAX_CLICK_TITLE_LENGTH = 30
const MAX_CLICK_CONTEXT_LENGTH = 25
const MAX_NO_RESULTS_QUERY_LENGTH = 160
const MAX_REFORMULATED_QUERY_LENGTH = 105
const MAX_RESULT_ID_LENGTH = 50
export type SearchSessionEndReason =
  | 'result_clicked'
  | 'query_changed'
  | 'query_invalid'
  | 'page_changed'
  | 'filter_changed'
  | 'sort_changed'
  | 'left_search_page'
  | 'results_refreshed'

export type SearchFailedErrorType = 'validation' | 'network' | 'server' | 'unknown'

export interface SearchDiagnosticResult {
  id?: string | null
  rootId?: string | null
  nodeType?: string | null
  title?: string | null
  subTitle?: string | null
  journalIssueTitle?: string | null
  chapterTitle?: string | null
  paragraphTitle?: string | null
  shortTitle?: string | null
  [key: string]: unknown
}

export interface SearchResultsRenderedPayloadInput {
  searchId: string
  query: string
  page: number
  rows: number
  totalResults: number
  results: SearchDiagnosticResult[]
  transportRetried?: boolean
}

export interface SearchNoResultsPayloadInput {
  hasFilters: boolean
  searchId: string
  query: string
  page: number
  totalResults: number
  resultCountOnPage: number
}

export interface SearchResultClickedPayloadInput {
  searchId: string
  query: string
  result: SearchDiagnosticResult
  rank: number
  page: number
  totalResults: number
  resultCountOnPage: number
  hasLicense: boolean
  isFirstClickForSearch: boolean
}

export interface SearchReformulatedPayloadInput {
  previousSearchId: string
  newSearchId: string
  previousQuery: string
  newQuery: string
  previousTotalResults: number
  previousResultCountOnPage: number
  previousClickedResult: boolean
  timeSincePreviousSearchMs: number
}

export interface SearchSessionEndedPayloadInput {
  searchId: string
  query: string
  page: number
  totalResults: number
  resultCountOnPage: number
  clickedResult: boolean
  resultClickCount: number
  firstClickedRank: number | null
  reformulated: boolean
  continuedSearch: boolean
  usedPagination: boolean
  changedFilters: boolean
  changedSort: boolean
  timeOnSerpMs: number
  endReason: SearchSessionEndReason
}

export interface SearchFailedPayloadInput {
  query: string
  page: number
  statusCode: number | null
  errorType: SearchFailedErrorType
  hasFilters: boolean
  filterCount: number
  sortOrder: string
}

export interface SearchSignatureInput {
  query: string
  page: number
  routeSearchSignature: string
  totalResults: number
  results: SearchDiagnosticResult[]
}

export function createSearchId(now = Date.now, random = Math.random): string {
  return `${now()}-${random().toString(36).slice(2, 8)}`
}

export function buildSearchSignature(input: SearchSignatureInput): string {
  return JSON.stringify({
    query: input.query,
    page: input.page,
    routeSearchSignature: input.routeSearchSignature,
    totalResults: input.totalResults,
    resultIds: input.results.map((result) => result.id ?? null),
  })
}

export function buildSearchResultsRenderedPayload(input: SearchResultsRenderedPayloadInput) {
  const duplicateVisibleResults = detectDuplicateVisibleResultGroups(input.results)
  const topResult = input.results[0]
  const maxQueryLength = input.transportRetried
    ? MAX_RETRIED_RENDERED_QUERY_LENGTH
    : MAX_RENDERED_QUERY_LENGTH

  return {
    searchId: input.searchId,
    query: truncatePendoString(input.query, maxQueryLength, maxQueryLength),
    queryLength: getQueryLength(input.query),
    queryTokenCount: getQueryTokenCount(input.query),
    page: input.page,
    rows: input.rows,
    totalResults: input.totalResults,
    resultCountOnPage: input.results.length,
    hasResults: input.results.length > 0,
    topResultId: truncatePendoString(topResult?.id, MAX_RESULT_ID_LENGTH, MAX_RESULT_ID_LENGTH),
    topResultNodeType: topResult?.nodeType ?? null,
    topResultTitle: truncatePendoString(
      topResult?.title,
      MAX_RENDERED_TITLE_LENGTH,
      MAX_RENDERED_TITLE_LENGTH,
    ),
    ...(input.transportRetried ? { transportRetried: true } : {}),
    ...duplicateVisibleResults,
  }
}

export function buildSearchNoResultsPayload(input: SearchNoResultsPayloadInput) {
  return {
    searchId: input.searchId,
    query: truncatePendoString(
      input.query,
      MAX_NO_RESULTS_QUERY_LENGTH,
      MAX_NO_RESULTS_QUERY_LENGTH,
    ),
    queryLength: getQueryLength(input.query),
    queryTokenCount: getQueryTokenCount(input.query),
    page: input.page,
    totalResults: input.totalResults,
    resultCountOnPage: input.resultCountOnPage,
    hasFilters: input.hasFilters,
  }
}

export function buildSearchResultClickedPayload(input: SearchResultClickedPayloadInput) {
  return {
    searchId: input.searchId,
    query: truncatePendoString(input.query, MAX_CLICK_QUERY_LENGTH, MAX_CLICK_QUERY_LENGTH),
    resultId: truncatePendoString(input.result.id, MAX_RESULT_ID_LENGTH, MAX_RESULT_ID_LENGTH),
    rootId: truncatePendoString(input.result.rootId, MAX_RESULT_ID_LENGTH, MAX_RESULT_ID_LENGTH),
    nodeType: input.result.nodeType ?? null,
    title: truncatePendoString(input.result.title, MAX_CLICK_TITLE_LENGTH, MAX_CLICK_TITLE_LENGTH),
    childContextTitle: truncatePendoString(
      input.result.subTitle ?? input.result.chapterTitle ?? input.result.paragraphTitle,
      MAX_CLICK_CONTEXT_LENGTH,
      MAX_CLICK_CONTEXT_LENGTH,
    ),
    rank: input.rank,
    page: input.page,
    totalResults: input.totalResults,
    resultCountOnPage: input.resultCountOnPage,
    hasLicense: input.hasLicense,
    isFirstClickForSearch: input.isFirstClickForSearch,
  }
}

export function buildSearchReformulatedPayload(input: SearchReformulatedPayloadInput) {
  return {
    previousSearchId: input.previousSearchId,
    newSearchId: input.newSearchId,
    previousQuery: truncatePendoString(
      input.previousQuery,
      MAX_REFORMULATED_QUERY_LENGTH,
      MAX_REFORMULATED_QUERY_LENGTH,
    ),
    newQuery: truncatePendoString(
      input.newQuery,
      MAX_REFORMULATED_QUERY_LENGTH,
      MAX_REFORMULATED_QUERY_LENGTH,
    ),
    previousTotalResults: input.previousTotalResults,
    previousResultCountOnPage: input.previousResultCountOnPage,
    previousClickedResult: input.previousClickedResult,
    timeSincePreviousSearchMs: input.timeSincePreviousSearchMs,
  }
}

export function detectDuplicateVisibleResultGroups(results: SearchDiagnosticResult[]) {
  const visibleKeyCounts = new Map<string, number>()

  for (const result of results) {
    const visibleKey = [result.nodeType, result.title, result.subTitle, result.journalIssueTitle]
      .filter(Boolean)
      .join('|')
      .toLowerCase()

    if (!visibleKey) {
      continue
    }

    visibleKeyCounts.set(visibleKey, (visibleKeyCounts.get(visibleKey) ?? 0) + 1)
  }

  const duplicateVisibleResultCount = Array.from(visibleKeyCounts.values()).filter(
    (count) => count > 1,
  ).length

  return {
    hasDuplicateVisibleResults: duplicateVisibleResultCount > 0,
    duplicateVisibleResultCount,
  }
}

function getQueryLength(query: string): number {
  return query.trim().length
}

function getQueryTokenCount(query: string): number {
  return query.trim().split(/\s+/).filter(Boolean).length
}
